import React, { useState, useEffect, useMemo } from "react";
import { useNotification } from "../../context/NotificationContext";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProps,
} from "@hello-pangea/dnd";
import { Top100OrganizerService } from "../../services/Top100OrganizerService";
import "../../styles/Admin.css";

const StrictDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) return null;
  return <Droppable {...props}>{children}</Droppable>;
};

interface Track {
  id: string;
  title: string;
  artistName?: string;
}

export const Top100Organizer = () => {
  const { showNotification } = useNotification();
  const [chartTracks, setChartTracks] = useState<Track[]>([]);
  const [libraryTracks, setLibraryTracks] = useState<Track[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasMounted, setHasMounted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setHasMounted(true);
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const chartData = await Top100OrganizerService.fetchCurrentChart();
      const allTracks =
        (await Top100OrganizerService.fetchAllTracks()) as Track[];
      const trackIDs = chartData?.chartTrackIDs || [];

      const inChart = trackIDs
        .map((id: string) => allTracks.find((t: Track) => t.id === id))
        .filter((t: Track | undefined): t is Track => !!t);

      const available = allTracks.filter((t) => !trackIDs.includes(t.id));

      setChartTracks(inChart);
      setLibraryTracks(available);
    } catch (err) {
      console.error("Failed to load tracks", err);
      showNotification("Failed to load initial tracks.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleShuffleGenerate = async () => {
    if (!window.confirm("Shuffle all tracks and update Top 100?")) return;
    setIsProcessing(true);
    try {
      await Top100OrganizerService.updateTop100Charts();
      await loadInitialData();
      showNotification("Top 100 successfully shuffled and generated!");
    } catch (err) {
      console.error(err);
      showNotification("Failed to shuffle charts.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveChanges = async () => {
    setIsProcessing(true);
    try {
      const ids = chartTracks.map((t) => t.id);
      await Top100OrganizerService.updateChartOrder(ids);
      showNotification("Changes saved successfully!");
    } catch (err) {
      console.error(err);
      showNotification("Failed to save layout changes.");
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredLibrary = useMemo(() => {
    return libraryTracks.filter(
      (t) =>
        t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.artistName?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [libraryTracks, searchTerm]);

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      const isChart = source.droppableId === "chart";
      const list = isChart ? [...chartTracks] : [...libraryTracks];
      const [removed] = list.splice(source.index, 1);
      list.splice(destination.index, 0, removed);
      isChart ? setChartTracks(list) : setLibraryTracks(list);
    } else {
      const sourceList =
        source.droppableId === "chart" ? [...chartTracks] : [...libraryTracks];
      const destList =
        destination.droppableId === "chart"
          ? [...chartTracks]
          : [...libraryTracks];

      const [removed] = sourceList.splice(source.index, 1);
      destList.splice(destination.index, 0, removed);

      if (source.droppableId === "chart") {
        setChartTracks(sourceList);
        setLibraryTracks(destList);
      } else {
        setLibraryTracks(sourceList);
        setChartTracks(destList);
      }
    }
  };

  if (!hasMounted || isLoading) {
    return (
      <div className="admin-container">
        <div className="spinner-btn"></div>
      </div>
    );
  }

  return (
    <div className="admin-section" style={{ marginTop: 24 }}>
      <header className="admin-header-row top100-header">
        <div className="admin-header-container">
          <span className="admin-augio-logo">AUGIO</span>
          <h1 className="admin-title">Top 100 Organizer</h1>
        </div>

        <div className="top100-actions">
          <button
            onClick={handleShuffleGenerate}
            className="btn-secondary"
            disabled={isProcessing}
          >
            {isProcessing ? "Processing..." : "Generate Top 100"}
          </button>

          <button
            onClick={handleSaveChanges}
            className="btn-primary"
            disabled={isProcessing}
          >
            Save Changes
          </button>
        </div>
      </header>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="admin-grid admin-grid--top100">
          {/* Chart Column */}
          <div className="admin-section top100-panel">
            <div className="top100-panel-header">
              <span className="section-header top100-panel-title">
                Active Chart ({chartTracks.length})
              </span>

              <button
                onClick={() => {
                  setChartTracks([]);
                  showNotification(
                    "Chart list cleared. Don't forget to save changes!",
                  ); // <-- Clean toast note
                }}
                className="top100-clear-btn"
                type="button"
              >
                Clear All
              </button>
            </div>

            <StrictDroppable droppableId="chart">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="top100-list"
                >
                  {chartTracks.map((track, index) => (
                    <Draggable
                      key={`chart-${track.id}`}
                      draggableId={`chart-${track.id}`}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="admin-input read-only top100-row"
                          style={provided.draggableProps.style}
                        >
                          <div className="top100-row-left">
                            <span className="top100-rank">{index + 1}</span>
                            <span>{track.title}</span>
                          </div>

                          <span className="top100-artist">
                            {track.artistName}
                          </span>
                        </div>
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </div>
              )}
            </StrictDroppable>
          </div>

          {/* Library Column */}
          <div className="admin-section top100-panel">
            <h2 className="section-header">Library (Newest First)</h2>

            <input
              className="admin-input"
              placeholder="Search tracks or artists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <StrictDroppable droppableId="library">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="top100-list"
                >
                  {filteredLibrary.map((track, index) => (
                    <Draggable
                      key={`lib-${track.id}`}
                      draggableId={`lib-${track.id}`}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="admin-input top100-row top100-row--library"
                          style={provided.draggableProps.style}
                        >
                          <div className="top100-lib-title">{track.title}</div>
                          <div className="top100-lib-artist">
                            {track.artistName}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </div>
              )}
            </StrictDroppable>
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};
